// Bilingual content dictionary. Hebrew (he) is the default; English (en) mirrors it.
// Structural data (ids, tech list) lives in ../data; user-facing copy lives here.
// The hero/section titles that animate word-by-word are stored as token arrays;
// a token with { grad: true } renders in the blue gradient.

export const LANGS = ["he", "en"];
export const DEFAULT_LANG = "he";
export const DIR_BY_LANG = { he: "rtl", en: "ltr" };

const translations = {
  he: {
    lang: { he: "עברית", en: "English", switchTo: "English", label: "החלפת שפה" },

    nav: {
      home: "בית",
      story: "הסיפור שלנו",
      services: "השירותים",
      blog: "בלוג",
      process: "איך עובדים",
      projects: "פרויקטים",
      faq: "שאלות נפוצות",
      contact: "צרו קשר",
      aiCta: "דברו עם ה־AI שלנו",
      primary: "ניווט ראשי",
      menu: "פתיחת תפריט",
      skip: "דלג לתוכן",
    },

    hero: {
      eyebrow: "CodeIL · Software House",
      title: [
        { t: "אנחנו" },
        { t: "בונים" },
        { t: "את" },
        { t: "התוכנה" },
        { t: "שמניעה", grad: true },
        { t: "את" },
        { t: "העסק" },
        { t: "שלכם." },
      ],
      lede:
        "בית תוכנה המספק פתרון מקצה לקצה. משלב האפיון והתכנון, דרך פיתוח מואץ מבוסס AI, ועד לפרודקשן. אנו בונים מערכות חכמות, באיכות ללא פשרות, ובשקיפות מלאה לכל אורך הדרך.",
      ctaPrimary: "ספרו ל־AI על הפרויקט שלכם",
      ctaSecondary: "ראו מקרי בוחן",
      scroll: "SCROLL",
      stats: [
        "ארכיטקטורה מתקדמת ושקופה",
        "פיתוח Web, Mobile ו־SaaS",
        "ליווי טכנולוגי כשותפים לדרך",
      ],
    },

    about: {
      eyebrow: "ABOUT US",
      title: "שותפים טכנולוגיים,\nלא רק מפתחים.",
      p1: "מערכות דיגיטליות הן הליבה של העסק. המטרה שלנו היא לקחת את החזון שלכם ולהפוך אותו למוצר יציב ומאובטח. אנחנו מטפלים בכל מחזור החיים של הפיתוח, משלב הרעיון ועד לתחזוקה בסביבת ייצור.",
      p2: "הגישה שלנו פשוטה: פתרון טכנולוגי תחת קורת גג אחת. על ידי הטמעת כלי בינה מלאכותית מתקדמים בתהליכי הפיתוח שלנו, אנו מצליחים לייעל משמעותית את העבודה, לקצר זמנים ולהעניק לכם ערך מוסף עצום — בלי להתפשר על ארכיטקטורה נקייה.",
      whys: [
        { title: "סטנדרט פיתוח גבוה", desc: "הטכנולוגיות החדישות ביותר, מגויסות לבניית מערכות יציבות ומהירות — לא לרשימת קניות." },
        { title: "אמינות ושקיפות", desc: "אתם תמיד יודעים היכן הפרויקט עומד. שקיפות מלאה, ניהול פרויקטים מוקפד ותקשורת רציפה משלב האפיון ועד להשקה המוצלחת." },
        { title: "מחויבות להצלחה", desc: "אנחנו לא נעלמים אחרי העלייה לאוויר. נשארים לתמוך, לנטר ולהגדיל יחד איתכם." },
        { title: "התאמה לכל קנה מידה", desc: "ממערכת ניהול פשוטה לעסק ועד ארכיטקטורת Microservices מלאה לסטארטאפ בצמיחה." },
      ],
      panelFile: "architecture.cs",
      firstVersionLabel: "FIRST VERSION",
      firstVersion: "2–4 שבועות",
      afterLaunchLabel: "AFTER LAUNCH",
      afterLaunch: "ליווי מלא",
    },

    services: {
      eyebrow: "WHAT WE BUILD",
      title: "ארבעה תחומים.\nמוצר אחד שעובד.",
      lede: "תהליך ברור, שקיפות מלאה ותוצרים שאפשר למדוד — לא הבטחות.",
      items: [
        {
          title: "פיתוח מערכות Web ו־SaaS",
          desc: "בניית פלטפורמות ענן ומערכות ניהול מורכבות, מאפס ועד למוצר מוגמר.",
          points: [
            "אפיון טכני וארכיטקטורת מסד נתונים",
            "פיתוח צד שרת וצד לקוח ברמת Enterprise",
            "מוצרי SaaS מרובי־לקוחות (Multi-tenant)",
          ],
        },
        {
          title: "פיתוח אפליקציות Mobile",
          desc: "אפליקציות מהירות, יציבות ובעלות חוויית משתמש חלקה ל־iOS ול־Android.",
          points: [
            "תכנון חוויית משתמש (UX/UI) למובייל",
            "פיתוח נייטיב או קרוס־פלטפורם",
            "חיבור למצלמה, מיקום ושירותי צד־שלישי",
          ],
        },
        {
          title: "אינטגרציות AI ואוטומציה",
          desc: "בינה מלאכותית שמייעלת תהליכים, מנתחת נתונים וחוסכת עלויות תפעול אמיתיות.",
          points: [
            "הטמעת מודלי שפה (LLMs) במערכות ליבה",
            "אוטומציה של קריאה וסיווג מסמכים",
            "צ'אטבוטים וסוכנים חכמים (AI Agents)",
          ],
        },
        {
          title: "שירותי פיתוח וייעוץ לחברות",
          desc: "תגבור צוותים טכנולוגיים ושירותי Chief Architect לחברות ולסטארטאפים.",
          points: [
            "שירותי פרילנס ופיתוח לפי דרישה",
            "שדרוג ושיפור קוד קיים (Refactoring)",
            "ייעוץ ובניית אסטרטגיית פיתוח",
          ],
        },
      ],
    },

    process: {
      eyebrow: "HOW WE BUILD",
      title: "מהשיחה הראשונה ועד לאוויר —\nבלי רגע אחד של עמימות.",
      steps: [
        { name: "Discover", desc: "אפיון מדויק, הבנת הצרכים העסקיים ובניית אסטרטגיה טכנולוגית שרואה את התמונה המלאה, ומכינה את הקרקע לפיתוח מהיר ויעיל המותאם אליכם." },
        { name: "Architecture", desc: "תכנון ארכיטקטורת התוכנה, בחירת Tech Stack ובניית תשתית סקיילאבילית — כדי שהצמיחה שלכם לא תישבר על הקוד." },
        { name: "Build", desc: "פיתוח אג'ילי ושקוף עם דמו כל שבועיים. כותבים קוד נקי, שומרים על תקשורת רציפה, ואתם רואים את המוצר גדל בזמן אמת." },
        { name: "Launch", desc: "עלייה לאוויר אחרי בדיקות ביצועים, מובייל ונגישות. ואז — ניטור בזמן אמת ותמיכה שוטפת, כי הצמיחה מתחילה דווקא כאן." },
      ],
    },

    projects: {
      eyebrow: "CASE STUDIES",
      title: "איך תרגמנו אתגר עסקי לתוצאה מדידה.",
      lede: "שלושה פרויקטים, אותו עיקרון: להבין את הבעיה לעומק לפני שכותבים שורת קוד אחת.",
      challengeLabel: "האתגר",
      solutionLabel: "הפתרון",
      outcomeLabel: "התוצאה",
      items: [
        {
          tag: "FINTECH / AI SAAS",
          name: "Finly AI",
          challenge: "אוטומציה פיננסית שסורקת אימיילים ומזהה חשבוניות ברמת דיוק מוחלטת — אפס פספוסים.",
          solution: "פלטפורמת SaaS על ‎.NET 9 בארכיטקטורת CQRS נקייה, מנוע סיווג מסמכים מבוסס AI ותצוגה מהירה ב־React.",
          outcome: "אודיט מלא לכל החלטת AI, וייעול דרמטי של עבודת הנהלת החשבונות.",
        },
        {
          tag: "PROPTECH / REAL ESTATE",
          name: "Radarize AI",
          challenge: "פלטפורמת נדל\"ן רחבה עם הרשאות מורכבות, חשיפה פרטית של נכסים וסנכרון נתונים מהיר.",
          solution: "ארכיטקטורת Microservices עם YARP Gateway, ו־Frontend מורכב ב־React לניהול זרימות המידע.",
          outcome: "מוצר B2B יציב וסקיילבילי, מוכן לעומסי משתמשים אמיתיים.",
        },
        {
          tag: "HR / PAYROLL",
          name: "איזי תלוש",
          challenge: "פלטפורמה קיימת שנדרשה לחיזוק טכנולוגי, שדרוג תהליכי פיתוח ושיפור חוויית הלקוח.",
          solution: "תגבור טכנולוגי במיקור חוץ: כניסה מהירה לקוד הקיים, ייצוב המערכת ופיתוח פיצ'רים חדשים.",
          outcome: "קיצור זמני פיתוח, יציבות גבוהה יותר וערך שמגיע מהר יותר למשתמשי הקצה.",
        },
      ],
    },

    ai: {
      eyebrow: "PROJECT SCOPING",
      title: "במקום טופס —\nשיחה עם ארכיטקט.",
      lede: "תארו את הרעיון במילים שלכם, ו־CodeIL Architect יחזיר לכם כיוון טכנולוגי ראשוני, הערכת מורכבות והשאלות שכדאי לשאול — עוד לפני שדיברנו.",
      responseLabel: "RESPONSE",
      response: "מיידי, 24/7",
      commitmentLabel: "COMMITMENT",
      commitment: "אפס התחייבות",
      panelTitle: "CodeIL Architect",
      statusConnected: "מחובר ל־AI Hub",
      statusConnecting: "מתחבר ל־AI Hub...",
      statusError: "שגיאת חיבור. מנסים להתחבר מחדש",
      statusIdle: "ממתין להתחברות",
      greeting: "שלום! אני CodeIL Architect. ספרו לי במשפט אחד מה אתם רוצים לבנות — ואחזיר לכם כיוון טכנולוגי ראשוני.",
      chips: ["מערכת ניהול לעסק", "אפליקציה למובייל", "אוטומציה עם AI", "שדרוג מערכת קיימת"],
      inputPlaceholder: "לדוגמה: אני צריך מערכת SaaS לשוק הנדל״ן",
      send: "שילחו",
      you: "אתם",
      architect: "CodeIL Architect",
      sending: "שולח...",
    },

    faq: {
      eyebrow: "FAQ",
      title: "שאלות שאנחנו\nנשאלים הכי הרבה.",
      items: [
        { q: "איך נראה תהליך העבודה מהפגישה הראשונה ועד העלייה לאוויר?", a: "מתחילים בשיחת אפיון קצרה, ממשיכים למסמך כיוונים ברור עם לוחות זמנים, מאשרים עיצוב ופיתוח בשלבים, ועולים לאוויר רק אחרי בדיקות ביצועים, מובייל ונגישות." },
        { q: "תוך כמה זמן אפשר לראות אתר או מערכת באוויר?", a: "ברוב הפרויקטים תראו גרסה ראשונה תוך 2–4 שבועות. העלייה המלאה תלויה בהיקף — דפי נחיתה מהירים יותר ממערכות מורכבות." },
        { q: "מה קורה אחרי העלייה לאוויר?", a: "אפשר להמשיך איתנו למסלול תחזוקה הכולל ניטור, עדכוני אבטחה, שיפורי UX שוטפים ותמיכה בתקלות — כדי לשמור על יציבות וצמיחה לאורך זמן." },
        { q: "איך מתמחרים פרויקט ומה משפיע על העלות?", a: "העלות נקבעת לפי היקף מסכים, אינטגרציות, מורכבות לוגית ולוח זמנים. אחרי האפיון תקבלו הצעת מחיר שקופה עם פירוק שלבי עבודה." },
      ],
    },

    contact: {
      eyebrow: "CONTACT",
      title: "מעדיפים פשוט לדבר?",
      lede: "השאירו כמה פרטים ונחזור אליכם עם הכוונה מקצועית, הצעת כיוון ראשונית ותשובות לכל שאלה. בלי מכירות אגרסיביות.",
      email: "info@codeil.org",
      phone: "050-2215233",
      namePlaceholder: "שם מלא",
      emailPlaceholder: 'כתובת דוא"ל',
      phonePlaceholder: "מספר טלפון (לא חובה)",
      messagePlaceholder: "ספרו לנו בקצרה על הצורך שלכם",
      submit: "שלחו הודעה",
      submitting: "שולחים...",
      successTitle: "ההודעה נשלחה בהצלחה",
      successBody: "תודה שפניתם אלינו. נחזור אליכם בהקדם האפשרי.",
      errName: "יש להזין שם מלא",
      errEmailRequired: 'יש להזין כתובת דוא"ל',
      errEmailInvalid: 'כתובת הדוא"ל אינה תקינה',
      errMessageRequired: "יש להזין הודעה",
      errMessageShort: "ההודעה צריכה להכיל לפחות 20 תווים",
      errSubmit: "אירעה שגיאה בשליחה. נסו שוב בעוד מספר רגעים.",
    },

    story: {
      eyebrow: "OUR STORY",
      title: [
        { t: "מביאים" }, { t: "את" }, { t: "הרעיונות" }, { t: "שלכם" },
        { t: "לאוויר", grad: true }, { t: "–" }, { t: "חכם" }, { t: "יותר" },
        { t: "ומהר" }, { t: "יותר." },
      ],
      lede: "אנחנו בית תוכנה המספק פתרון הוליסטי. על ידי שילוב של כלי AI בתהליכי העבודה שלנו, אנו מעניקים ללקוחותינו מעטפת פיתוח משתלמת, מקיפה ואיכותית, שלוקחת רעיונות והופכת אותם למערכות עובדות בסביבת ייצור.",
      stats: [
        "מערכות שעלו לאוויר",
        "עד גרסה ראשונה בממוצע",
        "מהפרויקטים עם ליווי אחרי השקה",
        "מחזור דמו קבוע ללקוח",
      ],
      whoEyebrow: "WHO WE ARE",
      whoTitle: "פתרון הנדסי\nמקצה לקצה.",
      whoP1: "העבודה איתנו מרכזת את כל צרכי הפיתוח שלכם למקום אחד (One-Stop-Shop). משלב הארכיטקטורה, דרך עיצוב וחוויית משתמש, ועד לקידוד, תמיכה והשקה. הכל מתבצע בסטנדרט הגבוה ביותר.",
      whoP2: "אנו רותמים את כלי הפיתוח והבינה המלאכותית המתקדמים בעולם כדי לספק פרויקטים מורכבים בזמן שיא, תוך מתן ערך גבוה והקפדה על איכות הארכיטקטורה ללא פשרות.",
      timelineEyebrow: "TIMELINE",
      timelineTitle: "איך הגענו לכאן.",
      timeline: [
        { tag: "התחלה", title: "התחלה מקצועית וממוקדת", desc: "התחלנו מפרויקט אחד ללקוח אחד, עם החלטה פשוטה: לא לקחת עבודה שאנחנו לא מאמינים שנוכל לעשות מצוין." },
        { tag: "התמחות", title: "ארכיטקטורה הפכה לחתימה שלנו", desc: "ככל שהפרויקטים גדלו, הבנו שהערך האמיתי שלנו הוא בשלב שלפני הקוד — Clean Architecture, CQRS ו־Microservices שמחזיקים גם כשהמוצר מכפיל את עצמו." },
        { tag: "AI", title: "הכנסנו בינה מלאכותית למוצרים אמיתיים", desc: "לא דמואים — מנועי סיווג מסמכים, אוטומציות ו־AI Agents שרצים בייצור עם אודיט מלא לכל החלטה, כמו ב־Finly AI." },
        { tag: "היום", title: "שותפים טכנולוגיים לעסקים בצמיחה", desc: "מלווים עסקים קטנים ובינוניים מהרעיון ועד לסקייל — כולל תגבור צוותים ושירותי Chief Architect לחברות שכבר יש להן פיתוח פנימי." },
      ],
      principlesEyebrow: "PRINCIPLES",
      principlesTitle: "חמישה כללים שאנחנו לא שוברים.",
      principles: [
        { title: "מסבירים לפני שמחליטים", desc: "כל בחירה טכנולוגית מגיעה עם הסבר בשפה שלכם ועם מה היא עולה בזמן ובכסף." },
        { title: "קוד שמישהו אחר יוכל לתחזק", desc: "אנחנו כותבים כאילו מחר הצוות הפנימי שלכם ממשיך מכאן. בלי קסמים, עם תיעוד." },
        { title: "דמו כל שבועיים, בלי יוצא מן הכלל", desc: "אתם רואים התקדמות אמיתית, לא דוח סטטוס. אפשר לשנות כיוון מוקדם ובזול." },
        { title: "לא מוכרים מה שלא צריך", desc: "אם אפשר לפתור את זה בכלי קיים בחצי תקציב — נגיד לכם, גם אם זה על חשבוננו." },
        { title: "נשארים אחרי ההשקה", desc: "יום העלייה לאוויר הוא לא סוף הפרויקט אלא תחילת החיים שלו. ניטור, עדכוני אבטחה ושיפורים שוטפים — כדי שהמערכת תגדל יחד עם העסק." },
      ],
      ctaTitle: "בואו נבנה משהו שיחזיק.",
      ctaLede: "ספרו ל־CodeIL Architect על הרעיון שלכם וקבלו כיוון טכנולוגי ראשוני — בלי התחייבות.",
      ctaPrimary: "דברו עם ה־AI שלנו",
      ctaSecondary: "לכל השירותים",
    },

    servicesPage: {
      eyebrow: "SERVICES",
      title: [
        { t: "כל" }, { t: "מה" }, { t: "שצריך" }, { t: "כדי" }, { t: "להביא" }, { t: "מוצר" },
        { t: "לאוויר.", grad: true },
      ],
      lede: "ארבעה תחומי ליבה, מודלי עבודה גמישים וסטאק אחד שאנחנו מכירים לעומק. בוחרים את מה שמתאים לשלב שאתם נמצאים בו — ולא משלמים על השאר.",
      blocks: [
        {
          num: "01", chip: "WEB & SAAS",
          title: "פיתוח מערכות\nWeb ו־SaaS",
          desc: "פלטפורמות ענן ומערכות ניהול מורכבות — מאפיון ראשוני ועד מוצר שמשרת אלפי משתמשים.",
          tags: [".NET 9", "React", "PostgreSQL", "Azure"],
          rows: [
            { title: "אפיון טכני וארכיטקטורת מסד נתונים", desc: "מיפוי זרימות, מודל נתונים והרשאות — השלב שבו נחתכות רוב עלויות הפיתוח העתידיות." },
            { title: "פיתוח צד שרת ברמת Enterprise", desc: "Clean Architecture ו־CQRS, API מתועד, ובדיקות שמחזיקות את המערכת כשהיא גדלה." },
            { title: "מוצרי SaaS מרובי־לקוחות", desc: "ארכיטקטורת Multi-tenant עם הפרדת נתונים, חיוב, ותשתית שמאפשרת לצרף לקוח חדש בלחיצה." },
            { title: "מערכות ניהול ופורטלים פנימיים", desc: "כלים תפעוליים שמחליפים גיליונות אקסל ותהליכים ידניים — עם דוחות והרשאות מדויקות." },
          ],
        },
        {
          num: "02", chip: "MOBILE",
          title: "פיתוח אפליקציות\nMobile",
          desc: "אפליקציות מהירות ויציבות ל־iOS ול־Android, עם חוויית משתמש שמרגישה נייטיב.",
          tags: ["React Native", "Push", "Offline-first"],
          rows: [
            { title: "תכנון חוויית משתמש למובייל", desc: "מסכים שנבנים סביב תרחיש השימוש האמיתי — לא גרסה מוקטנת של האתר." },
            { title: "פיתוח נייטיב או קרוס־פלטפורם", desc: "בוחרים לפי הצורך שלכם ולא לפי אופנה: קוד משותף כשזה חוסך, נייטיב כשזה קריטי." },
            { title: "חיבור לחומרה ולשירותי צד־שלישי", desc: "מצלמה, מיקום, סליקה, התראות ואינטגרציות — כולל טיפול במצבי קצה וניתוקי רשת." },
            { title: "העלאה לחנויות וליווי גרסאות", desc: "מטפלים בכל תהליך הפרסום, ואחר כך בעדכונים ובמעקב אחרי קריסות בזמן אמת." },
          ],
        },
        {
          num: "03", chip: "AI & AUTOMATION",
          title: "אינטגרציות AI\nואוטומציה",
          desc: "בינה מלאכותית שנכנסת לתהליך העבודה הקיים ומחזירה שעות עבודה — לא הדגמה יפה.",
          tags: ["LLMs", "RAG", "AI Agents", "Audit Trail"],
          rows: [
            { title: "הטמעת מודלי שפה במערכות ליבה", desc: "חיבור LLM לנתונים שלכם עם שליטה בעלויות, בזמני תגובה ובגבולות ההרשאות." },
            { title: "קריאה וסיווג אוטומטי של מסמכים", desc: "חשבוניות, טפסים והזמנות שנכנסים למערכת לבד — עם אודיט מלא לכל החלטה, כמו ב־Finly AI." },
            { title: "צ'אטבוטים וסוכנים חכמים", desc: "עוזרים שמכירים את המוצר והנתונים שלכם, לשירות לקוחות או לצוותים פנימיים." },
            { title: "אוטומציה של תהליכים תפעוליים", desc: "מיפוי המקומות שבהם הצוות עושה עבודה ידנית חוזרת, והחלפתם בזרימות אוטומטיות." },
          ],
        },
        {
          num: "04", chip: "CONSULTING",
          title: "שירותי פיתוח\nוייעוץ לחברות",
          desc: "תגבור צוותים קיימים וליווי ארכיטקטוני לחברות ולסטארטאפים שכבר בונים בעצמם.",
          tags: ["Outsourcing", "Refactoring", "CI/CD"],
          rows: [
            { title: "תגבור צוות טכנולוגי", desc: "מפתחים שנכנסים מהר לקוד קיים ומתחילים לייצר ערך תוך ימים, לא חודשים." },
            { title: "שירותי Chief Architect", desc: "ליווי ארכיטקטוני שוטף לחברות שאין להן ארכיטקט פנימי — כולל סקירות קוד והחלטות תשתית." },
            { title: "שדרוג ושיפור קוד קיים", desc: "ייצוב מערכת שהתחילה לחרוק, ניקוי חוב טכני והחזרת מהירות הפיתוח לצוות." },
            { title: "בניית אסטרטגיית פיתוח", desc: "מפת דרכים טכנולוגית שמתאימה לתקציב ולשלב העסקי, עם סדר עדיפויות ברור." },
          ],
        },
      ],
      modelsEyebrow: "ENGAGEMENT MODELS",
      modelsTitle: "שלוש דרכים לעבוד איתנו.",
      modelsLede: "בוחרים לפי מידת הוודאות שיש לכם על המוצר ועל לוח הזמנים.",
      models: [
        { key: "FIXED SCOPE", title: "פרויקט בהיקף סגור", desc: "אפיון מלא מראש, מחיר ולוח זמנים ידועים. מתאים כשהמוצר ברור ורוצים ודאות תקציבית.", fit: "מתאים ל: אתרים, מערכות ניהול, MVP מוגדר" },
        { key: "RETAINER", title: "צוות קבוע חודשי", desc: "כמות שעות קבועה בחודש עם סדר עדיפויות שנקבע יחד. מתאים למוצר חי שממשיך להתפתח.", fit: "מתאים ל: SaaS בצמיחה, פיתוח מתמשך" },
        { key: "ADVISORY", title: "ליווי ארכיטקטוני", desc: "אנחנו לא כותבים את הקוד — אנחנו מוודאים שהוא נכתב נכון. סקירות, החלטות ומפת דרכים.", fit: "מתאים ל: חברות עם צוות פיתוח פנימי" },
      ],
      ctaTitle: "לא בטוחים איזה שירות מתאים?",
      ctaLede: "תארו את הצורך ל־CodeIL Architect ותקבלו המלצה על מודל העבודה והסטאק — בלי התחייבות.",
      ctaPrimary: "דברו עם ה־AI שלנו",
      ctaSecondary: "ראו מקרי בוחן",
    },

    blog: {
      eyebrow: "THE BLOG",
      title: [
        { t: "מה" }, { t: "שלמדנו" }, { t: "מבניית", grad: true }, { t: "מערכות" }, { t: "אמיתיות." },
      ],
      lede: "כתיבה טכנית בגובה העיניים על ארכיטקטורה, AI ותהליכי פיתוח — מהניסיון שלנו בשטח, בלי באזזוורדס.",
      newsletterLabel: "NEWSLETTER",
      newsletterText: "פוסט אחד בחודש. בלי ספאם, בלי מכירות.",
      newsletterPlaceholder: 'כתובת דוא"ל',
      newsletterSubmit: "הרשמה",
      newsletterDone: "נרשמתם ✓",
      allLabel: "הכל",
      countOne: "כתבה אחת",
      countMany: "כתבות",
      featuredBadge: "מומלץ",
      featuredMeta: "ארכיטקטורה · 8 דק' קריאה",
      featuredTitle: "למה רוב הפרויקטים נשברים בשנה השנייה — ומה עושים כדי למנוע את זה",
      featuredExcerpt: "הבעיה כמעט אף פעם לא בקוד עצמו אלא בהחלטות שהתקבלו בשבועיים הראשונים. סקירה של ההחלטות הארכיטקטוניות שקובעות אם המערכת תשרוד את הצמיחה.",
      featuredCta: "קראו את הפוסט",
      readSuffix: "דק' קריאה",
      emptyText: "אין עדיין פוסטים בקטגוריה הזו.",
      posts: [
        { cat: "AI", title: "איך מכניסים LLM למערכת ייצור בלי לאבד שליטה על העלויות", excerpt: "קאשינג, בחירת מודל לפי משימה ותקרות שימוש — שלוש החלטות שמורידות חשבון חודשי בעשרות אחוזים.", date: "מרץ 2026", read: "6" },
        { cat: "ארכיטקטורה", title: "Multi-tenant: שלוש דרכים להפריד נתונים ומתי כל אחת נכונה", excerpt: "מסד נפרד, סכימה נפרדת או עמודת tenant — השוואה מעשית לפי עלות, אבטחה וקצב גיוס לקוחות.", date: "פברואר 2026", read: "7" },
        { cat: "תהליך", title: "מה באמת צריך להיות במסמך אפיון (וממה אפשר לוותר)", excerpt: "אפיון טוב הוא לא מאה עמודים. הנה המבנה שאנחנו משתמשים בו לפני כל פרויקט חדש.", date: "פברואר 2026", read: "5" },
        { cat: "עסקי", title: "כמה באמת עולה מערכת — ולמה קשה לתת מספר בשיחה הראשונה", excerpt: "פירוק של הגורמים שקובעים תקציב פיתוח, ואיך לצמצם אי־ודאות לפני שחותמים.", date: "ינואר 2026", read: "4" },
        { cat: "AI", title: "אודיט להחלטות AI: איך בונים מערכת שאפשר להסביר לרואה חשבון", excerpt: "מה תיעדנו בכל שלב בסיווג חשבוניות ב־Finly AI, ולמה זה היה תנאי להטמעה בכלל.", date: "ינואר 2026", read: "9" },
        { cat: "תהליך", title: "להיכנס לקוד של מישהו אחר: מדריך לשבוע הראשון", excerpt: "איך אנחנו ממפים מערכת קיימת, מזהים סיכונים ומתחילים לייצר ערך בלי לשבור כלום.", date: "דצמבר 2025", read: "6" },
      ],
      ctaTitle: "יש נושא שהייתם רוצים שנכתוב עליו?",
      ctaLede: "כתבו לנו — הפוסטים הכי טובים שלנו נולדו משאלות של לקוחות.",
      ctaButton: "שלחו לנו רעיון",
    },

    footer: {
      tagline: "פתרונות דיגיטליים ממוקדי תוצאה לעסקים שרוצים לגדול מהר, חכם ובנראות גבוהה.",
      colServices: "שירותים",
      colCompany: "חברה",
      colLegal: "משפטי",
      allServices: "כל השירותים",
      caseStudies: "מקרי בוחן",
      startProject: "תחילת פרויקט",
      privacy: "מדיניות פרטיות",
      terms: "תנאי שימוש",
      rights: "© 2026 CodeIL. כל הזכויות שמורות.",
      stamp: "BUILT IN TEL AVIV · {IL}",
    },

    seo: {
      title: "CodeIL · בית תוכנה — פיתוח Web, Mobile, SaaS ואינטגרציות AI",
      description:
        "בית תוכנה שבונה מערכות Web, Mobile ו־SaaS עם ארכיטקטורה נקייה, אינטגרציות AI וליווי טכנולוגי כשותפים לדרך.",
    },
  },

  en: {
    lang: { he: "עברית", en: "English", switchTo: "עברית", label: "Switch language" },

    nav: {
      home: "Home",
      story: "Our Story",
      services: "Services",
      blog: "Blog",
      process: "How We Work",
      projects: "Projects",
      faq: "FAQ",
      contact: "Contact",
      aiCta: "Talk to our AI",
      primary: "Primary navigation",
      menu: "Open menu",
      skip: "Skip to content",
    },

    hero: {
      eyebrow: "CodeIL · Software House",
      title: [
        { t: "We" },
        { t: "build" },
        { t: "the" },
        { t: "software" },
        { t: "that" },
        { t: "powers", grad: true },
        { t: "your" },
        { t: "business." },
      ],
      lede:
        "An end-to-end software house. From specification and planning, through AI-accelerated development, to production. We build smart systems with uncompromising quality and full transparency along the way.",
      ctaPrimary: "Tell the AI about your project",
      ctaSecondary: "See case studies",
      scroll: "SCROLL",
      stats: [
        "Advanced, transparent architecture",
        "Web, Mobile & SaaS development",
        "A technology partner for the long run",
      ],
    },

    about: {
      eyebrow: "ABOUT US",
      title: "Technology partners,\nnot just developers.",
      p1: "Digital systems are the core of the business. Our goal is to turn your vision into a stable and secure product. We handle the entire development lifecycle, from concept to production maintenance.",
      p2: "Our approach is simple: a complete technological solution under one roof. By integrating advanced AI tools into our development process, we significantly streamline work, shorten timelines, and deliver immense value — without compromising on clean architecture.",
      whys: [
        { title: "A high development standard", desc: "The latest technologies, put to work building stable, fast systems — not padding a shopping list." },
        { title: "Reliability and transparency", desc: "You always know where the project stands. Full transparency, strict project management, and continuous communication from spec to successful launch." },
        { title: "Committed to the outcome", desc: "We don't disappear after launch. We stay to support, monitor and scale alongside you." },
        { title: "Built for any scale", desc: "From a simple business management system to a full microservices architecture for a growing startup." },
      ],
      panelFile: "architecture.cs",
      firstVersionLabel: "FIRST VERSION",
      firstVersion: "2–4 weeks",
      afterLaunchLabel: "AFTER LAUNCH",
      afterLaunch: "Full support",
    },

    services: {
      eyebrow: "WHAT WE BUILD",
      title: "Four disciplines.\nOne product that works.",
      lede: "A clear process, full transparency, and results you can measure — not promises.",
      items: [
        {
          title: "Web & SaaS systems",
          desc: "Building cloud platforms and complex management systems, from zero to a finished product.",
          points: [
            "Technical specification & database architecture",
            "Enterprise-grade back-end and front-end",
            "Multi-tenant SaaS products",
          ],
        },
        {
          title: "Mobile app development",
          desc: "Fast, stable apps with a smooth user experience for iOS and Android.",
          points: [
            "Mobile UX/UI design",
            "Native or cross-platform development",
            "Camera, location & third-party integrations",
          ],
        },
        {
          title: "AI integrations & automation",
          desc: "Artificial intelligence that streamlines processes, analyzes data and saves real operating costs.",
          points: [
            "Embedding LLMs into core systems",
            "Automated document reading & classification",
            "Chatbots and smart AI agents",
          ],
        },
        {
          title: "Development & consulting for companies",
          desc: "Team augmentation and Chief Architect services for companies and startups.",
          points: [
            "Freelance and on-demand development",
            "Refactoring and improving existing code",
            "Consulting and development strategy",
          ],
        },
      ],
    },

    process: {
      eyebrow: "HOW WE BUILD",
      title: "From the first call to launch —\nwithout a single moment of ambiguity.",
      steps: [
        { name: "Discover", desc: "Precise specification, understanding business needs, and building a technology strategy that sees the big picture, paving the way for fast and efficient development tailored to you." },
        { name: "Architecture", desc: "Designing the software architecture, choosing the tech stack and building a scalable foundation — so your growth doesn't break on the code." },
        { name: "Build", desc: "Agile, transparent development with a demo every two weeks. We write clean code, keep communication constant, and you watch the product grow in real time." },
        { name: "Launch", desc: "Going live after performance, mobile and accessibility testing. Then — real-time monitoring and ongoing support, because growth really starts here." },
      ],
    },

    projects: {
      eyebrow: "CASE STUDIES",
      title: "How we turned a business challenge into a measurable result.",
      lede: "Three projects, one principle: understand the problem deeply before writing a single line of code.",
      challengeLabel: "The challenge",
      solutionLabel: "The solution",
      outcomeLabel: "The outcome",
      items: [
        {
          tag: "FINTECH / AI SAAS",
          name: "Finly AI",
          challenge: "Financial automation that scans emails and identifies invoices with absolute accuracy — zero misses.",
          solution: "A SaaS platform on .NET 9 in a clean CQRS architecture, an AI-based document classification engine, and a fast React front-end.",
          outcome: "A full audit trail for every AI decision, and a dramatic boost to accounting productivity.",
        },
        {
          tag: "PROPTECH / REAL ESTATE",
          name: "Radarize AI",
          challenge: "A broad real-estate platform with complex permissions, private property exposure and fast data sync.",
          solution: "A microservices architecture with a YARP Gateway, and a complex React front-end managing the data flows.",
          outcome: "A stable, scalable B2B product, ready for real user load.",
        },
        {
          tag: "HR / PAYROLL",
          name: "Easy Tlush",
          challenge: "An existing platform that needed a technology boost, upgraded dev processes and a better customer experience.",
          solution: "Outsourced technology augmentation: fast onboarding into the existing code, stabilizing the system and building new features.",
          outcome: "Shorter development cycles, higher stability, and value delivered faster to end users.",
        },
      ],
    },

    ai: {
      eyebrow: "PROJECT SCOPING",
      title: "Instead of a form —\na conversation with an architect.",
      lede: "Describe the idea in your own words, and CodeIL Architect will hand you an initial technical direction, a complexity estimate and the questions worth asking — before we've even spoken.",
      responseLabel: "RESPONSE",
      response: "Instant, 24/7",
      commitmentLabel: "COMMITMENT",
      commitment: "Zero commitment",
      panelTitle: "CodeIL Architect",
      statusConnected: "Connected to AI Hub",
      statusConnecting: "Connecting to AI Hub...",
      statusError: "Connection error. Reconnecting",
      statusIdle: "Waiting to connect",
      greeting: "Hi! I'm CodeIL Architect. Tell me in one sentence what you want to build — and I'll hand you an initial technical direction.",
      chips: ["A business management system", "A mobile app", "Automation with AI", "Upgrading an existing system"],
      inputPlaceholder: "e.g. I need a SaaS system for real estate",
      send: "Send",
      you: "You",
      architect: "CodeIL Architect",
      sending: "Sending...",
    },

    faq: {
      eyebrow: "FAQ",
      title: "The questions we\nget asked the most.",
      items: [
        { q: "What does the process look like, from the first meeting to launch?", a: "We start with a short scoping call, move to a clear direction document with timelines, approve design and development in stages, and go live only after performance, mobile and accessibility testing." },
        { q: "How quickly can a site or system go live?", a: "In most projects you'll see a first version within 2–4 weeks. Full launch depends on scope — landing pages are faster than complex systems." },
        { q: "What happens after launch?", a: "You can continue with us on a maintenance track including monitoring, security updates, ongoing UX improvements and issue support — to keep the system stable and growing over time." },
        { q: "How is a project priced, and what affects the cost?", a: "Cost is set by the number of screens, integrations, logical complexity and timeline. After scoping you'll get a transparent quote with a breakdown of work stages." },
      ],
    },

    contact: {
      eyebrow: "CONTACT",
      title: "Prefer to just talk?",
      lede: "Leave a few details and we'll get back to you with professional guidance, an initial direction and answers to any question. No aggressive sales.",
      email: "info@codeil.org",
      phone: "050-2215233",
      namePlaceholder: "Full name",
      emailPlaceholder: "Email address",
      phonePlaceholder: "Phone number (optional)",
      messagePlaceholder: "Tell us briefly about what you need",
      submit: "Send message",
      submitting: "Sending...",
      successTitle: "Your message was sent",
      successBody: "Thanks for reaching out. We'll get back to you as soon as possible.",
      errName: "Please enter your full name",
      errEmailRequired: "Please enter an email address",
      errEmailInvalid: "That email address isn't valid",
      errMessageRequired: "Please enter a message",
      errMessageShort: "The message should be at least 20 characters",
      errSubmit: "Something went wrong. Please try again in a few moments.",
    },

    story: {
      eyebrow: "OUR STORY",
      title: [
        { t: "Bringing" }, { t: "your" }, { t: "ideas" },
        { t: "live", grad: true }, { t: "–" }, { t: "smarter" }, { t: "and" }, { t: "faster." },
      ],
      lede: "We are an end-to-end software house. By integrating AI tools into our workflows, we provide our clients with a comprehensive, highly cost-effective, and quality development envelope that turns ideas into systems running in production.",
      stats: [
        "systems shipped to production",
        "to a first version on average",
        "of projects with post-launch support",
        "steady demo cycle per client",
      ],
      whoEyebrow: "WHO WE ARE",
      whoTitle: "End-to-end\nengineering solution.",
      whoP1: "Working with us centralizes all your development needs in one place (One-Stop-Shop). From architecture, through design and UX, to coding, support, and launch. Everything is executed at the highest standard.",
      whoP2: "We harness the world's most advanced development and AI tools to deliver complex projects in record time, providing high value while strictly maintaining uncompromising architectural quality.",
      timelineEyebrow: "TIMELINE",
      timelineTitle: "How we got here.",
      timeline: [
        { tag: "THE START", title: "A professional and focused start", desc: "We started with one project for one client, with a simple rule: don't take work we don't believe we can do brilliantly." },
        { tag: "FOCUS", title: "Architecture became our signature", desc: "As projects grew, we realized our real value is in the stage before the code — Clean Architecture, CQRS and microservices that hold up even when the product doubles." },
        { tag: "AI", title: "We brought AI into real products", desc: "Not demos — document-classification engines, automations and AI agents running in production with a full audit trail for every decision, like in Finly AI." },
        { tag: "TODAY", title: "Technology partners for growing businesses", desc: "We guide small and mid-sized businesses from idea to scale — including team augmentation and Chief Architect services for companies that already have in-house development." },
      ],
      principlesEyebrow: "PRINCIPLES",
      principlesTitle: "Five rules we never break.",
      principles: [
        { title: "Explain before deciding", desc: "Every technical choice comes with an explanation in your language and what it costs in time and money." },
        { title: "Code someone else can maintain", desc: "We write as if your in-house team continues from here tomorrow. No magic, fully documented." },
        { title: "A demo every two weeks, no exceptions", desc: "You see real progress, not a status report. You can change direction early and cheaply." },
        { title: "We don't sell what you don't need", desc: "If it can be solved with an existing tool at half the budget — we'll tell you, even at our own expense." },
        { title: "We stay after launch", desc: "Go-live day isn't the end of the project but the start of its life. Monitoring, security updates and ongoing improvements — so the system grows with the business." },
      ],
      ctaTitle: "Let's build something that lasts.",
      ctaLede: "Tell CodeIL Architect about your idea and get an initial technical direction — no commitment.",
      ctaPrimary: "Talk to our AI",
      ctaSecondary: "All services",
    },

    servicesPage: {
      eyebrow: "SERVICES",
      title: [
        { t: "Everything" }, { t: "it" }, { t: "takes" }, { t: "to" }, { t: "ship" }, { t: "a" }, { t: "product" },
        { t: "live.", grad: true },
      ],
      lede: "Four core disciplines, flexible engagement models and one stack we know deeply. Pick what fits the stage you're at — and don't pay for the rest.",
      blocks: [
        {
          num: "01", chip: "WEB & SAAS",
          title: "Web & SaaS\ndevelopment",
          desc: "Cloud platforms and complex management systems — from initial spec to a product serving thousands of users.",
          tags: [".NET 9", "React", "PostgreSQL", "Azure"],
          rows: [
            { title: "Technical spec & database architecture", desc: "Mapping flows, the data model and permissions — the stage where most future development costs are decided." },
            { title: "Enterprise-grade back-end", desc: "Clean Architecture and CQRS, a documented API, and tests that hold the system together as it grows." },
            { title: "Multi-tenant SaaS products", desc: "Multi-tenant architecture with data isolation, billing, and infrastructure to onboard a new client in one click." },
            { title: "Internal admin systems & portals", desc: "Operational tools that replace spreadsheets and manual processes — with precise reports and permissions." },
          ],
        },
        {
          num: "02", chip: "MOBILE",
          title: "Mobile app\ndevelopment",
          desc: "Fast, stable apps for iOS and Android, with a user experience that feels native.",
          tags: ["React Native", "Push", "Offline-first"],
          rows: [
            { title: "Mobile UX design", desc: "Screens built around the real usage scenario — not a shrunk version of the website." },
            { title: "Native or cross-platform", desc: "We choose by your needs, not by trend: shared code where it saves, native where it's critical." },
            { title: "Hardware & third-party integrations", desc: "Camera, location, payments, notifications and integrations — including edge cases and network drops." },
            { title: "Store submission & release support", desc: "We handle the whole publishing process, then updates and real-time crash monitoring." },
          ],
        },
        {
          num: "03", chip: "AI & AUTOMATION",
          title: "AI integrations\n& automation",
          desc: "AI that fits into your existing workflow and returns work hours — not a pretty demo.",
          tags: ["LLMs", "RAG", "AI Agents", "Audit Trail"],
          rows: [
            { title: "Embedding LLMs into core systems", desc: "Connecting an LLM to your data with control over cost, response time and permission boundaries." },
            { title: "Automated document reading & classification", desc: "Invoices, forms and orders that enter the system on their own — with a full audit of every decision, like Finly AI." },
            { title: "Chatbots and smart agents", desc: "Assistants that know your product and data, for customer service or internal teams." },
            { title: "Operational process automation", desc: "Mapping where the team does repetitive manual work, and replacing it with automated flows." },
          ],
        },
        {
          num: "04", chip: "CONSULTING",
          title: "Development &\nconsulting for companies",
          desc: "Augmenting existing teams and architectural guidance for companies and startups already building.",
          tags: ["Outsourcing", "Refactoring", "CI/CD"],
          rows: [
            { title: "Tech team augmentation", desc: "Developers who ramp into existing code fast and start delivering value in days, not months." },
            { title: "Chief Architect services", desc: "Ongoing architectural guidance for companies without an in-house architect — code reviews and infra decisions included." },
            { title: "Upgrading existing code", desc: "Stabilizing a system that started to creak, clearing technical debt and restoring the team's velocity." },
            { title: "Building a development strategy", desc: "A technology roadmap that fits the budget and business stage, with clear priorities." },
          ],
        },
      ],
      modelsEyebrow: "ENGAGEMENT MODELS",
      modelsTitle: "Three ways to work with us.",
      modelsLede: "Choose by how much certainty you have about the product and the timeline.",
      models: [
        { key: "FIXED SCOPE", title: "Fixed-scope project", desc: "Full spec upfront, known price and timeline. Fits when the product is clear and you want budget certainty.", fit: "Fits: websites, admin systems, a defined MVP" },
        { key: "RETAINER", title: "Monthly retained team", desc: "A fixed number of hours per month with priorities set together. Fits a living product that keeps evolving.", fit: "Fits: growing SaaS, ongoing development" },
        { key: "ADVISORY", title: "Architectural guidance", desc: "We don't write the code — we make sure it's written right. Reviews, decisions and a roadmap.", fit: "Fits: companies with an in-house dev team" },
      ],
      ctaTitle: "Not sure which service fits?",
      ctaLede: "Describe your need to CodeIL Architect and get a recommendation on the engagement model and stack — no commitment.",
      ctaPrimary: "Talk to our AI",
      ctaSecondary: "See case studies",
    },

    blog: {
      eyebrow: "THE BLOG",
      title: [
        { t: "What" }, { t: "we" }, { t: "learned" }, { t: "from" }, { t: "building", grad: true }, { t: "real" }, { t: "systems." },
      ],
      lede: "Plain-spoken technical writing on architecture, AI and development process — from our experience in the field, no buzzwords.",
      newsletterLabel: "NEWSLETTER",
      newsletterText: "One post a month. No spam, no sales.",
      newsletterPlaceholder: "Email address",
      newsletterSubmit: "Subscribe",
      newsletterDone: "Subscribed ✓",
      allLabel: "All",
      countOne: "1 article",
      countMany: "articles",
      featuredBadge: "Featured",
      featuredMeta: "Architecture · 8 min read",
      featuredTitle: "Why most projects break in year two — and what to do to prevent it",
      featuredExcerpt: "The problem is almost never the code itself but the decisions made in the first two weeks. A review of the architectural decisions that determine whether the system survives growth.",
      featuredCta: "Read the post",
      readSuffix: "min read",
      emptyText: "No posts in this category yet.",
      posts: [
        { cat: "AI", title: "How to put an LLM into a production system without losing control of costs", excerpt: "Caching, choosing a model per task and usage caps — three decisions that cut the monthly bill by tens of percent.", date: "Mar 2026", read: "6" },
        { cat: "Architecture", title: "Multi-tenant: three ways to separate data and when each is right", excerpt: "Separate database, separate schema or a tenant column — a practical comparison by cost, security and client-acquisition pace.", date: "Feb 2026", read: "7" },
        { cat: "Process", title: "What really belongs in a spec (and what you can skip)", excerpt: "A good spec isn't a hundred pages. Here's the structure we use before every new project.", date: "Feb 2026", read: "5" },
        { cat: "Business", title: "What a system really costs — and why it's hard to give a number on the first call", excerpt: "A breakdown of the factors that set a development budget, and how to reduce uncertainty before signing.", date: "Jan 2026", read: "4" },
        { cat: "AI", title: "Auditing AI decisions: building a system you can explain to an accountant", excerpt: "What we logged at each step of invoice classification in Finly AI, and why it was a precondition for adoption at all.", date: "Jan 2026", read: "9" },
        { cat: "Process", title: "Getting into someone else's code: a first-week guide", excerpt: "How we map an existing system, spot risks and start delivering value without breaking anything.", date: "Dec 2025", read: "6" },
      ],
      ctaTitle: "Is there a topic you'd like us to write about?",
      ctaLede: "Write to us — our best posts were born from client questions.",
      ctaButton: "Send us an idea",
    },

    footer: {
      tagline: "Results-driven digital solutions for businesses that want to grow fast, smart and highly visible.",
      colServices: "Services",
      colCompany: "Company",
      colLegal: "Legal",
      allServices: "All services",
      caseStudies: "Case studies",
      startProject: "Start a project",
      privacy: "Privacy policy",
      terms: "Terms of service",
      rights: "© 2026 CodeIL. All rights reserved.",
      stamp: "BUILT IN TEL AVIV · {IL}",
    },

    seo: {
      title: "CodeIL · Software House — Web, Mobile, SaaS & AI",
      description:
        "A software house building Web, Mobile and SaaS systems with clean architecture, AI integrations and a true technology partnership.",
    },
  },
};

export default translations;
